/**
 * LocalPFormDialog — Create/Edit Local Purchase contract.
 * 6 tabs: General, Pricing, Logistics, Forward, Intermediary, Flags & Notes.
 * RHF + yup conditional validation theo BR-LP-2/3/7.
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateLocalPMutation,
  useUpdateLocalPMutation,
} from '@/store/api/localPApi'
import type { LocalPCreateIn, LocalPDetail } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import GeneralTab from './localp-form-tabs/GeneralTab'
import PricingTab from './localp-form-tabs/PricingTab'
import LogisticsTab from './localp-form-tabs/LogisticsTab'
import ForwardTab from './localp-form-tabs/ForwardTab'
import IntermediaryTab from './localp-form-tabs/IntermediaryTab'
import FlagsNotesTab from './localp-form-tabs/FlagsNotesTab'
import { EMPTY_LOCALP, type LocalPFormValues } from './localp-form-tabs/types'

export interface LocalPFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: LocalPDetail | null
  onClose: () => void
  onSaved?: (contractNo: string) => void
}

/** yup schema — conditional theo contract_type và is_intermediary. */
const schema: yup.ObjectSchema<LocalPFormValues> = yup.object({
  contract_no: yup.string().trim().default('').max(20),
  contract_date: yup.string().required('Bắt buộc nhập ngày hợp đồng'),
  cp_code: yup.string().trim().required('Bắt buộc chọn vendor'),
  seller_ref: yup.string().trim().default('').max(50),
  quality_code: yup.string().trim().default(''),
  quality_des_contract: yup.string().trim().default(''),
  certification: yup.string().trim().default(''),
  me_or_ib: yup
    .mixed<'ME' | 'IB' | ''>()
    .oneOf(['ME', 'IB', ''] as const)
    .default(''),
  license_cert_no: yup.string().trim().default(''),
  quantity_kg: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập quantity', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Quantity phải > 0', (v) => typeof v === 'number' && v > 0)
    .required('Bắt buộc nhập quantity'),
  no_of_bag: yup.mixed<number | ''>().default(''),
  package: yup.string().trim().default(''),

  contract_type: yup
    .mixed<'Outright' | 'Differential'>()
    .oneOf(['Outright', 'Differential'] as const)
    .required(),
  outright_price: yup.mixed<number | ''>().when('contract_type', {
    is: 'Outright',
    then: (s) =>
      s
        .test('required', 'Bắt buộc nhập Outright Price', (v) => v !== '' && v !== undefined && v !== null)
        .test('positive', 'Outright Price phải > 0', (v) => typeof v === 'number' && v > 0),
    otherwise: (s) => s.default(''),
  }),
  ptbf_diffs: yup.mixed<number | ''>().when('contract_type', {
    is: 'Differential',
    then: (s) =>
      s.test('required', 'Bắt buộc nhập PTBF Differential', (v) => v !== '' && v !== undefined && v !== null),
    otherwise: (s) => s.default(''),
  }),
  price_unit: yup.string().trim().default(''),
  vat_rate: yup.mixed<number | ''>().default(''),
  fx: yup.mixed<number | ''>().default(''),
  fixed_fx: yup.mixed<number | ''>().default(''),
  futures_month: yup.string().trim().when('contract_type', {
    is: 'Differential',
    then: (s) => s.required('Bắt buộc chọn Futures Month'),
    otherwise: (s) => s.default(''),
  }),
  fixation_call: yup.string().trim().when('contract_type', {
    is: 'Differential',
    then: (s) => s.required('Bắt buộc chọn Fixation Call'),
    otherwise: (s) => s.default(''),
  }),

  latest_delivery: yup
    .string()
    .default('')
    .test(
      'after-contract-date',
      'Latest Delivery phải >= Contract Date',
      function (value) {
        const cd = this.parent.contract_date as string
        if (!value || !cd) return true
        return value >= cd
      },
    ),
  delivery_term: yup.string().trim().default(''),
  delivery_place_code: yup.string().trim().default(''),
  logistic_cost: yup.mixed<number | ''>().default(''),
  trader_in_charge: yup.string().trim().default(''),

  provi_market_rate: yup.mixed<number | ''>().default(''),
  stoploss_rate: yup.mixed<number | ''>().default(''),
  margin_value_per: yup.mixed<number | ''>().default(''),
  margin_limit_per: yup.mixed<number | ''>().default(''),
  time_limit: yup.mixed<number | ''>().default(''),
  margin_bonus_per: yup.mixed<number | ''>().default(''),
  margin_interest_month: yup.mixed<number | ''>().default(''),
  deposit_repayment_date: yup.string().default(''),

  is_intermediary: yup.boolean().default(false),
  shipper_code: yup.string().trim().when('is_intermediary', {
    is: true,
    then: (s) => s.required('Bắt buộc chọn Shipper khi có trung gian'),
    otherwise: (s) => s.default(''),
  }),
  contract_shipper_intermediary: yup.string().trim().default(''),
  outright_shipper_intermediary: yup.mixed<number | ''>().default(''),
  ptbfdiffs_shipper_intermediary: yup.mixed<number | ''>().default(''),

  is_issued: yup.boolean().default(false),
  is_sent_mail: yup.boolean().default(false),
  is_received_mail: yup.boolean().default(false),
  is_sent_paper: yup.boolean().default(false),
  is_received_paper: yup.boolean().default(false),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<LocalPFormValues>

const TAB_LABELS = [
  'General',
  'Pricing',
  'Logistics',
  'Forward',
  'Intermediary',
  'Flags & Notes',
]

/** Map LocalPFormValues -> LocalPCreateIn (chuyển '' thành null). */
function toApiPayload(v: LocalPFormValues): LocalPCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())

  return {
    contract_no: str(v.contract_no),
    contract_date: v.contract_date,
    cp_code: v.cp_code.trim(),
    seller_ref: str(v.seller_ref),
    quality_code: str(v.quality_code),
    quality_des_contract: str(v.quality_des_contract),
    certification: str(v.certification),
    me_or_ib: v.me_or_ib === '' ? null : v.me_or_ib,
    license_cert_no: str(v.license_cert_no),
    quantity_kg: typeof v.quantity_kg === 'number' ? v.quantity_kg : 0,
    no_of_bag: num(v.no_of_bag),
    package: str(v.package),

    contract_type: v.contract_type,
    outright_price: num(v.outright_price),
    ptbf_diffs: num(v.ptbf_diffs),
    price_unit: str(v.price_unit),
    vat_rate: num(v.vat_rate),
    fx: num(v.fx),
    fixed_fx: num(v.fixed_fx),
    futures_month: str(v.futures_month),
    fixation_call: str(v.fixation_call),

    latest_delivery: str(v.latest_delivery),
    delivery_term: str(v.delivery_term),
    delivery_place_code: str(v.delivery_place_code),
    logistic_cost: num(v.logistic_cost),
    trader_in_charge: str(v.trader_in_charge),

    provi_market_rate: num(v.provi_market_rate),
    stoploss_rate: num(v.stoploss_rate),
    margin_value_per: num(v.margin_value_per),
    margin_limit_per: num(v.margin_limit_per),
    time_limit: num(v.time_limit),
    margin_bonus_per: num(v.margin_bonus_per),
    margin_interest_month: num(v.margin_interest_month),
    deposit_repayment_date: str(v.deposit_repayment_date),

    is_intermediary: v.is_intermediary,
    shipper_code: str(v.shipper_code),
    contract_shipper_intermediary: str(v.contract_shipper_intermediary),
    outright_shipper_intermediary: num(v.outright_shipper_intermediary),
    ptbfdiffs_shipper_intermediary: num(v.ptbfdiffs_shipper_intermediary),

    is_issued: v.is_issued,
    is_sent_mail: v.is_sent_mail,
    is_received_mail: v.is_received_mail,
    is_sent_paper: v.is_sent_paper,
    is_received_paper: v.is_received_paper,

    note: str(v.note),
  }
}

/** Convert detail từ BE sang form values (null -> '' / 0 -> ''). */
function fromDetail(d: LocalPDetail): LocalPFormValues {
  const n = (x: number | null | undefined): number | '' =>
    x === null || x === undefined ? '' : x
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    contract_no: s(d.contract_no),
    contract_date: s(d.contract_date),
    cp_code: s(d.cp_code),
    seller_ref: s(d.seller_ref),
    quality_code: s(d.quality_code),
    quality_des_contract: s(d.quality_des_contract),
    certification: s(d.certification),
    me_or_ib: d.me_or_ib ?? '',
    license_cert_no: s(d.license_cert_no),
    quantity_kg: n(d.quantity_kg),
    no_of_bag: n(d.no_of_bag),
    package: s(d.package),

    contract_type: (d.contract_type as 'Outright' | 'Differential') ?? 'Outright',
    outright_price: n(d.outright_price),
    ptbf_diffs: n(d.ptbf_diffs),
    price_unit: s(d.price_unit),
    vat_rate: n(d.vat_rate),
    fx: n(d.fx),
    fixed_fx: n(d.fixed_fx),
    futures_month: s(d.futures_month),
    fixation_call: s(d.fixation_call),

    latest_delivery: s(d.latest_delivery),
    delivery_term: s(d.delivery_term),
    delivery_place_code: s(d.delivery_place_code),
    logistic_cost: n(d.logistic_cost),
    trader_in_charge: s(d.trader_in_charge),

    provi_market_rate: n(d.provi_market_rate),
    stoploss_rate: n(d.stoploss_rate),
    margin_value_per: n(d.margin_value_per),
    margin_limit_per: n(d.margin_limit_per),
    time_limit: n(d.time_limit),
    margin_bonus_per: n(d.margin_bonus_per),
    margin_interest_month: n(d.margin_interest_month),
    deposit_repayment_date: s(d.deposit_repayment_date),

    is_intermediary: !!d.is_intermediary,
    shipper_code: s(d.shipper_code),
    contract_shipper_intermediary: s(d.contract_shipper_intermediary),
    outright_shipper_intermediary: n(d.outright_shipper_intermediary),
    ptbfdiffs_shipper_intermediary: n(d.ptbfdiffs_shipper_intermediary),

    is_issued: !!d.is_issued,
    is_sent_mail: !!d.is_sent_mail,
    is_received_mail: !!d.is_received_mail,
    is_sent_paper: !!d.is_sent_paper,
    is_received_paper: !!d.is_received_paper,
    note: s(d.note),
  }
}

export function LocalPFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: LocalPFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [tab, setTab] = useState(0)
  const [createLocalP, { isLoading: creating }] = useCreateLocalPMutation()
  const [updateLocalP, { isLoading: updating }] = useUpdateLocalPMutation()
  const loading = creating || updating

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<LocalPFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY_LOCALP,
  })

  const contractType = watch('contract_type')
  const isIntermediary = watch('is_intermediary')
  const cpCode = watch('cp_code')

  useEffect(() => {
    if (!open) return
    setTab(0)
    if (isEdit && initialValue) {
      reset(fromDetail(initialValue))
    } else {
      reset(EMPTY_LOCALP)
    }
  }, [open, isEdit, initialValue, reset])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApiPayload(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateLocalP({
            contractNo: initialValue.contract_no,
            body: payload,
          }).unwrap()
          snack.success(`Đã cập nhật hợp đồng ${initialValue.contract_no}`)
          onSaved?.(res.data?.contract_no ?? initialValue.contract_no)
        } else {
          const res = await createLocalP(payload).unwrap()
          const newNo = res.data?.contract_no ?? ''
          snack.success(`Đã tạo hợp đồng ${newNo}`)
          onSaved?.(newNo)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Cập nhật thất bại' : 'Tạo hợp đồng thất bại'),
        )
      }
    },
    () => {
      snack.error('Vui lòng kiểm tra các trường còn lỗi (xem các tab)')
    },
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEdit ? `Sửa hợp đồng ${initialValue?.contract_no ?? ''}` : 'Tạo Local Purchase'}
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TAB_LABELS.map((l) => (
            <Tab key={l} label={l} />
          ))}
        </Tabs>
        <Box component="form" noValidate sx={{ pt: 2 }}>
          {tab === 0 && (
            <GeneralTab
              control={control}
              errors={errors}
              isEdit={isEdit}
              cpCode={cpCode}
            />
          )}
          {tab === 1 && (
            <PricingTab control={control} errors={errors} contractType={contractType} />
          )}
          {tab === 2 && <LogisticsTab control={control} errors={errors} />}
          {tab === 3 && <ForwardTab control={control} errors={errors} />}
          {tab === 4 && (
            <IntermediaryTab
              control={control}
              errors={errors}
              isIntermediary={!!isIntermediary}
            />
          )}
          {tab === 5 && <FlagsNotesTab control={control} errors={errors} />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LocalPFormDialog
